<template>
  <v-autocomplete
    class="coro-scrollable-combobox"
    :model-value="modelValue"
    :label="label"
    :aria-label="label"
    :items="items"
    :loading="loading"
    :error-messages="errorMessages"
    :custom-filter="filterOption"
    item-title="tld"
    item-value="tld"
    :item-props="getItemProps"
    variant="outlined"
    :persistent-placeholder="false"
    multiple
    chips
    closable-chips
    :return-object="false"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #message="{ message }">
      <div class="d-flex align-center">
        <v-icon icon="icon-warning-xs" color="red-faint" size="18" class="mr-1" />
        <span>{{ message }}</span>
      </div>
    </template>

    <template #chip="{ props: chipProps, item }">
      <v-chip
        v-bind="chipProps"
        :closable="true"
        variant="flat"
        size="default"
        :color="getChipColor(item.value)"
        close-icon="$closeCircle"
        :text="item.value"
      />
    </template>

    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" title="" :data-testid="`tld-option-${item.raw.tld}`">
        <div class="d-flex align-center justify-space-between w-100">
          <span class="body2 text--semibold">{{ item.raw.tld }}</span>

          <div class="d-flex align-center text-indigo-medium">
            <v-tooltip v-if="getDisabledTooltip(item.raw)" location="top" max-width="340">
              <template #activator="{ props: tooltipProps }">
                <v-icon
                  v-bind="tooltipProps"
                  icon="$infoXs"
                  size="18"
                  class="mr-1 flex-shrink-0 tld-option__info"
                  :aria-label="getDisabledTooltip(item.raw)"
                />
              </template>
              <span v-html="getDisabledTooltip(item.raw)" />
            </v-tooltip>

            <span class="body2">{{ getOptionName(item.raw) }}</span>
          </div>
        </div>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import escape from "lodash/escape";
import { TldOptionState } from "@/constants/phishing";
import type { TldFieldOption } from "@/_helpers/allow-block-list";

export default defineComponent({
  name: "AllowBlockTldField",
  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    items: {
      type: Array as PropType<TldFieldOption[]>,
      default: () => [],
    },
    errorMessages: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    invalidTerms: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props) {
    const i18n = useI18n();

    const getOptionName = (option: TldFieldOption) =>
      option.name ?? i18n.t("modals.allowBlockList.tlds.restrictedFallback");

    const getDisabledTooltip = (option: TldFieldOption) => {
      if (option.state === TldOptionState.RESTRICTED) {
        // The message carries markup and is rendered with v-html, and an unknown
        // restricted TLD is raw backend text, so the interpolated value is escaped.
        return i18n.t("modals.allowBlockList.tlds.restrictedTooltip", {
          tld: escape(option.tld),
        });
      }
      if (option.state === TldOptionState.ALREADY_ON_LIST) {
        return i18n.t("modals.allowBlockList.tlds.alreadyOnListTooltip");
      }
      return "";
    };

    const getItemProps = (option: TldFieldOption) => ({
      disabled: option.state !== TldOptionState.AVAILABLE,
    });

    const filterOption = (_value: string, query: string, item?: { raw: TldFieldOption }) => {
      const option = item?.raw;
      if (!option) return false;

      const term = query.trim().toLowerCase();
      if (!term) return true;

      return (
        option.tld.toLowerCase().includes(term) ||
        (option.name?.toLowerCase().includes(term) ?? false)
      );
    };

    const getChipColor = (value: string) =>
      props.invalidTerms.includes(value) ? "error" : "indigo-faint";

    return {
      getOptionName,
      getDisabledTooltip,
      getItemProps,
      filterOption,
      getChipColor,
    };
  },
});
</script>

<style lang="scss" scoped>
.tld-option__info {
  pointer-events: auto;
}
</style>
